import { memo, useEffect, useRef, type CSSProperties, type MouseEvent, type PointerEvent, type ReactNode } from 'react';
import type { Attachment, CallPayload, Conversation, ReplyRef } from '../api/types';
import { IconDownload, IconFile, IconPhone, IconRetry, IconTrash, IconVideo } from '../components/Icons';
import { StatusIcon } from '../components/StatusIcon';
import { VoicePlayer } from '../components/VoicePlayer';
import { formatDuration, formatSize, formatTime } from '../lib/format';
import { groupReactions } from '../lib/reactions';
import { discardMessage, messageStatus, retryMessage, type MessageItem } from '../store/chat';

const URL_RE = /(https?:\/\/[^\s<>"']+[^\s<>"'.,;:!?)\]])/g;

function Linkified({ text }: { text: string }) {
  const parts = text.split(URL_RE);
  return (
    <>
      {parts.map((p, i) =>
        i % 2 === 1 ? (
          <a key={i} href={p} target="_blank" rel="noopener noreferrer">
            {p}
          </a>
        ) : (
          p
        ),
      )}
    </>
  );
}

function FileCard({ name, size, href, children }: { name: string; size?: number; href?: string; children?: ReactNode }) {
  const body = (
    <>
      <span className="file-icon">
        <IconFile />
      </span>
      <span className="file-info">
        <span className="file-name">{name}</span>
        <span className="file-size">
          {size != null ? formatSize(size) : ''}
          {children}
        </span>
      </span>
      {href && <IconDownload size={18} />}
    </>
  );
  return href ? (
    <a className="file-card" href={href} download={name}>
      {body}
    </a>
  ) : (
    <div className="file-card">{body}</div>
  );
}

function AttachmentView({ a, mine, onOpenImage }: { a: Attachment; mine: boolean; onOpenImage: (a: Attachment) => void }) {
  if (a.kind === 'audio') return <VoicePlayer src={a.url} durationMs={a.durationMs} mine={mine} />;
  if (a.kind === 'image') {
    return (
      <button className="media-thumb" onClick={() => onOpenImage(a)} aria-label={`Открыть ${a.fileName}`}>
        <img
          src={a.previewUrl ?? a.url}
          alt={a.fileName}
          loading="lazy"
          decoding="async"
          // Known size: the box is laid out before the image arrives, so the feed does not jump.
          style={a.width && a.height ? ({ '--ar': a.width / a.height } as CSSProperties) : undefined}
          className={a.width && a.height ? 'sized' : undefined}
        />
      </button>
    );
  }
  if (a.kind === 'video') {
    return (
      <div className="media-video">
        <video src={a.url} controls preload="metadata" playsInline />
        <a className="media-download" href={`${a.url}?download`} download={a.fileName} aria-label="Скачать">
          <IconDownload size={16} />
        </a>
      </div>
    );
  }
  return <FileCard name={a.fileName} size={a.size} href={`${a.url}?download`} />;
}

function PendingAttachment({ m }: { m: MessageItem }) {
  const p = m.pending!;
  const progress = p.status === 'sending' && p.progress != null && (
    <div className="progress">
      <div style={{ width: `${Math.round(p.progress * 100)}%` }} />
    </div>
  );
  if (p.voiceDurationMs != null && p.previewUrl) {
    return <VoicePlayer src={p.previewUrl} durationMs={p.voiceDurationMs} mine />;
  }
  if (p.previewUrl && p.fileType?.startsWith('image/')) {
    return (
      <div className="media-thumb pending">
        <img src={p.previewUrl} alt={p.fileName} />
        {progress}
      </div>
    );
  }
  if (p.previewUrl && p.fileType?.startsWith('video/')) {
    return (
      <div className="media-video pending">
        <video src={p.previewUrl} muted playsInline preload="metadata" />
        {progress}
      </div>
    );
  }
  return (
    <div>
      <FileCard name={p.fileName ?? 'Файл'} size={p.fileSize} />
      {progress}
    </div>
  );
}

const CALL_TEXT: Record<CallPayload['outcome'], [string, string]> = {
  // [for the caller, for the callee]
  completed: ['Исходящий звонок', 'Входящий звонок'],
  missed: ['Нет ответа', 'Пропущенный звонок'],
  rejected: ['Звонок отклонён', 'Звонок отклонён'],
  busy: ['Абонент занят', 'Пропущенный звонок'],
  cancelled: ['Отменённый звонок', 'Пропущенный звонок'],
  failed: ['Звонок не удался', 'Звонок не удался'],
};

function CallEntry({ m, mine }: { m: MessageItem; mine: boolean }) {
  const p = m.payload as CallPayload;
  const label = CALL_TEXT[p.outcome]?.[mine ? 0 : 1] ?? 'Звонок';
  const missed = !mine && ['missed', 'busy', 'cancelled'].includes(p.outcome);
  return (
    <div className="call-entry" data-key={m.clientId}>
      <span className={`call-pill${missed ? ' missed' : ''}`}>
        {p.media === 'video' ? <IconVideo size={15} /> : <IconPhone size={15} />}
        {p.media === 'video' ? `${label} (видео)` : label}
        {p.durationSec > 0 && ` · ${formatDuration(p.durationSec)}`}
        <span className="call-time">{formatTime(m.createdAt)}</span>
      </span>
    </div>
  );
}

/** One line describing a quoted message: its text, or what it contains. */
export function replyPreview(r: Pick<ReplyRef, 'kind' | 'text' | 'attachmentKind'>): string {
  if (r.text) return r.text;
  if (r.kind === 'call') return 'Звонок';
  switch (r.attachmentKind) {
    case 'image':
      return 'Фото';
    case 'video':
      return 'Видео';
    case 'audio':
      return 'Голосовое сообщение';
    case 'file':
      return 'Файл';
    default:
      return 'Сообщение';
  }
}

function ReplyQuote({ r, name, onJump }: { r: ReplyRef; name: string; onJump?: (id: string) => void }) {
  return (
    <button className="reply-quote" onClick={() => onJump?.(r.id)} aria-label={`Ответ на сообщение: ${name}`}>
      <span className="reply-name">{name}</span>
      <span className="reply-text">{replyPreview(r)}</span>
    </button>
  );
}

/** Where the menu was opened: the press point and the bubble (the menu is laid out around the bubble). */
export type MenuAnchor = { x: number; y: number; rect: DOMRect };

const DOUBLE_TAP_MS = 320;
const DOUBLE_TAP_SLOP = 12;
const LONG_PRESS_MS = 450;
const LONG_PRESS_SLOP = 10;
const INTERACTIVE = 'a, button, video, audio, input';

/** Where a gesture started: plain bubble content, a photo thumbnail (long press only), or a control (no gestures). */
const gestureTarget = (target: EventTarget): 'content' | 'media' | null => {
  if (!(target instanceof Element)) return null;
  const hit = target.closest(INTERACTIVE);
  if (!hit) return 'content';
  return hit.matches('button.media-thumb') ? 'media' : null;
};

/** Double tap → quick 👍, long press / right click → reaction menu. */
function useBubbleGestures(
  m: MessageItem,
  enabled: boolean,
  onToggleReaction: (m: MessageItem, emoji: string) => void,
  onOpenMenu: (m: MessageItem, anchor: MenuAnchor) => void,
) {
  const press = useRef<{ x: number; y: number; media: boolean; timer: ReturnType<typeof setTimeout>; stop: () => void } | null>(null);
  const longFired = useRef(false);
  // A long press on a photo is followed by a click that would open the lightbox.
  const suppressClick = useRef(false);
  const lastTap = useRef<{ t: number; x: number; y: number } | null>(null);

  const cancel = () => {
    if (!press.current) return;
    clearTimeout(press.current.timer);
    press.current.stop();
    press.current = null;
  };
  useEffect(() => cancel, []);

  if (!enabled) return {};

  const onPointerDown = (e: PointerEvent) => {
    longFired.current = false;
    suppressClick.current = false;
    const where = gestureTarget(e.target);
    if (e.button !== 0 || !e.isPrimary || !where) return;
    cancel();
    const { clientX: x, clientY: y } = e;
    const bubble = e.currentTarget as HTMLElement;
    // Scrolling the feed (or anything else) aborts the long press.
    const onScroll = () => cancel();
    window.addEventListener('scroll', onScroll, { capture: true, passive: true });
    const timer = setTimeout(() => {
      cancel();
      longFired.current = true;
      suppressClick.current = true;
      lastTap.current = null;
      // iOS may already have started selecting the word under the finger.
      window.getSelection()?.removeAllRanges();
      navigator.vibrate?.(10);
      onOpenMenu(m, { x, y, rect: bubble.getBoundingClientRect() });
    }, LONG_PRESS_MS);
    press.current = { x, y, media: where === 'media', timer, stop: () => window.removeEventListener('scroll', onScroll, { capture: true }) };
  };

  const onPointerMove = (e: PointerEvent) => {
    const p = press.current;
    if (p && Math.hypot(e.clientX - p.x, e.clientY - p.y) > LONG_PRESS_SLOP) cancel();
  };

  const onPointerUp = (e: PointerEvent) => {
    const p = press.current;
    cancel();
    if (longFired.current) {
      // The finger lifted after the menu opened: not a tap.
      longFired.current = false;
      return;
    }
    if (!p) return;
    if (p.media) {
      // A tap on a photo opens the lightbox; no double-tap like there.
      lastTap.current = null;
      return;
    }
    const now = performance.now();
    const prev = lastTap.current;
    if (prev && now - prev.t < DOUBLE_TAP_MS && Math.hypot(e.clientX - prev.x, e.clientY - prev.y) < DOUBLE_TAP_SLOP) {
      lastTap.current = null;
      navigator.vibrate?.(10);
      onToggleReaction(m, '👍');
    } else {
      lastTap.current = { t: now, x: e.clientX, y: e.clientY };
    }
  };

  const onContextMenu = (e: MouseEvent) => {
    if (!gestureTarget(e.target)) return;
    e.preventDefault();
    // Mobile browsers fire contextmenu on long press too; the menu is already open then.
    if (longFired.current) return;
    cancel();
    onOpenMenu(m, { x: e.clientX, y: e.clientY, rect: (e.currentTarget as HTMLElement).getBoundingClientRect() });
  };

  const onClickCapture = (e: MouseEvent) => {
    if (!suppressClick.current) return;
    suppressClick.current = false;
    e.preventDefault();
    e.stopPropagation();
  };

  return { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: cancel, onContextMenu, onClickCapture };
}

function Reactions({ m, myId, onToggle }: { m: MessageItem; myId: string | undefined; onToggle: (m: MessageItem, emoji: string) => void }) {
  const groups = groupReactions(m.reactions, myId);
  if (!groups.length) return null;
  return (
    <div className="reactions">
      {groups.map((g) => {
        const label = g.mine ? `Ваша реакция: ${g.emoji}, всего ${g.count}` : `Реакция ${g.emoji}, всего ${g.count}`;
        return (
          <button
            key={g.emoji}
            className={`reaction${g.mine ? ' mine' : ''}`}
            aria-pressed={g.mine}
            aria-label={label}
            title={label}
            onClick={() => onToggle(m, g.emoji)}
          >
            <span className="reaction-emoji">{g.emoji}</span>
            {g.count > 1 && <span className="reaction-count">{g.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

export const MessageBubble = memo(function MessageBubble({
  m,
  mine,
  conv,
  myId,
  onOpenImage,
  onToggleReaction,
  onOpenMenu,
  onJumpTo,
}: {
  m: MessageItem;
  mine: boolean;
  conv: Conversation | undefined;
  myId: string | undefined;
  onOpenImage: (a: Attachment) => void;
  onToggleReaction: (m: MessageItem, emoji: string) => void;
  onOpenMenu: (m: MessageItem, anchor: MenuAnchor) => void;
  onJumpTo?: (messageId: string) => void;
}) {
  const gestures = useBubbleGestures(m, !m.pending && (m.kind === 'text' || m.kind === 'media'), onToggleReaction, onOpenMenu);
  if (m.kind === 'call') return <CallEntry m={m} mine={mine} />;
  if (m.kind !== 'text' && m.kind !== 'media') return null;
  const status = mine ? messageStatus(m, conv) : null;
  const text: string | undefined = m.payload?.text;
  // A pending upload is drawn from the local file; a pending forward already has the original's attachments.
  const local = !!m.pending && m.attachments.length === 0;
  const isVoice = local ? m.pending!.voiceDurationMs != null : m.attachments[0]?.kind === 'audio';
  const hasMedia = m.kind === 'media' && !isVoice;
  const nameOf = (id: string | null) => (id && id === myId ? 'Вы' : (conv?.peer.displayName ?? ''));
  return (
    <div className={`msg-row ${mine ? 'mine' : 'theirs'}`} data-key={m.clientId}>
      <div className={`bubble${hasMedia ? ' has-media' : ''}${status === 'failed' ? ' failed' : ''}`} {...gestures}>
        {m.forwardedFrom && (
          <div className="forwarded">
            Переслано от <b>{m.forwardedFrom.name}</b>
          </div>
        )}
        {m.replyTo && <ReplyQuote r={m.replyTo} name={nameOf(m.replyTo.senderId)} onJump={onJumpTo} />}
        {isVoice && (local ? <PendingAttachment m={m} /> : <AttachmentView a={m.attachments[0]} mine={mine} onOpenImage={onOpenImage} />)}
        {hasMedia && (
          <div className="attachments">
            {local ? <PendingAttachment m={m} /> : m.attachments.map((a) => <AttachmentView key={a.id} a={a} mine={mine} onOpenImage={onOpenImage} />)}
          </div>
        )}
        {text && (
          <div className="text">
            <Linkified text={text} />
          </div>
        )}
        <span className="meta">
          {formatTime(m.createdAt)}
          {status && <StatusIcon status={status} />}
        </span>
      </div>
      {!m.pending && <Reactions m={m} myId={myId} onToggle={onToggleReaction} />}
      {m.pending?.status === 'failed' && (
        <div className="msg-actions">
          <span className="msg-error">{m.pending.error ?? 'Не отправлено'}</span>
          <button className="icon-btn small" onClick={() => void retryMessage(m.clientId)} aria-label="Повторить">
            <IconRetry size={16} />
          </button>
          <button className="icon-btn small" onClick={() => void discardMessage(m.conversationId, m.clientId)} aria-label="Удалить">
            <IconTrash size={16} />
          </button>
        </div>
      )}
    </div>
  );
});
